import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { TextInput, Accordion, DatePicker } from '@/components/ui';
import Select from '@workpay/ds-shared/components/ui/Select';
import Stepper from '@/components/ui/Stepper/Stepper';
import type { StepConfig } from '@/components/ui/Stepper/types';
import StepperModalFooter from '@/components/shared/StepperModalFooter/StepperModalFooter';
import MandatoryFormRenderer from '@components/shared/MandatoryFormRenderer/MandatoryFormRenderer';
import useMultiStepForm from '@/hooks/useMultiStepForm';
import { useTranslation } from '@/hooks/useTranslation';
import { getCommercialTerms } from '@/services/commercialTermService';
import { getFormTemplateByScope, filterFormTemplate } from '@/services/formTemplateService';
import { createEmployee } from '@/services/employeeService';
import { useDepartmentsForCascade } from '@features/departments/hooks/useDepartmentsForCascade';
import { useJobTitlesForCascade } from '@features/job-titles/hooks/useJobTitlesForCascade';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@stores/notificationStore';
import { WorkpayApiError } from '@lib/apiEnvelope';
import type { FormTemplateTab } from '@/types/formTemplate';
import type { CreateEmployeePayload } from '@/types/employee';
import { toApiDateTime } from '@utils/dateHelpers';
import styles from './CreateEmployeeModal.module.scss';

// ============================================
// TYPES
// ============================================

interface CreateEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface EmployeeFormValues {
  firstName: string;
  lastName: string;
  preferredName: string;
  workEmail: string;
  country: string;
  // Department drives the JobTitle cascade — switching the dept clears the JT.
  // Both are required at the FE layer; BE V30 makes the columns nullable but
  // the product rule is "every employee has a job title under a department".
  departmentId: string;
  jobTitleId: string;
  employmentTypeId: string;
  contractType: string;
  startDate: string;
  grossSalary: string;
  salaryCurrency: string;
  salaryFrequency: string;
  probationMonths: string;
  weeklyWorkingHours: string;
}

const STEP_IDS = ['location', 'details', 'mandatory', 'templates'] as const;

const STEP_FIELDS: Record<number, (keyof EmployeeFormValues)[]> = {
  0: ['country', 'employmentTypeId'],
  1: ['firstName', 'lastName', 'workEmail', 'departmentId', 'jobTitleId', 'contractType', 'startDate', 'grossSalary', 'salaryCurrency', 'salaryFrequency'],
  2: [],
  3: [],
};

// Option values (labels translated inside component)
const CONTRACT_TYPE_VALUES = ['Permanent', 'Fixed Term', 'Casual'] as const;
const SALARY_FREQUENCY_VALUES = ['MONTHLY', 'BI_WEEKLY', 'WEEKLY'] as const;
const CURRENCY_VALUES = ['USD', 'EUR', 'GBP', 'KES', 'NGN', 'ZAR', 'INR'] as const;

const DEFAULT_FORM_VALUES: EmployeeFormValues = {
  firstName: '',
  lastName: '',
  preferredName: '',
  workEmail: '',
  country: '',
  departmentId: '',
  jobTitleId: '',
  employmentTypeId: '',
  contractType: '',
  startDate: '',
  grossSalary: '',
  salaryCurrency: '',
  salaryFrequency: '',
  probationMonths: '',
  weeklyWorkingHours: '',
};

// ============================================
// COMPONENT
// ============================================

function CreateEmployeeModal({ isOpen, onClose }: CreateEmployeeModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const session = useAuthStore((s) => s.session);
  const orgType = session?.organization.type ?? '';
  const orgId = session?.organization.id ?? '';

  const STEPS: StepConfig[] = useMemo(() => [
    { id: STEP_IDS[0], label: t('K_STEP_COUNTRY_EMPLOYMENT', 'Country & Employment Type') },
    { id: STEP_IDS[1], label: t('K_STEP_EMPLOYEE_DETAILS', 'Employee Details') },
    { id: STEP_IDS[2], label: t('K_STEP_MANDATORY_FORMS', 'Mandatory Forms') },
    { id: STEP_IDS[3], label: t('K_STEP_REQUEST_DATA', 'Request Data') },
  ], [t]);

  const CONTRACT_TYPE_OPTIONS = useMemo(() => [
    { label: t('K_OPTION_PERMANENT', 'Permanent'), value: CONTRACT_TYPE_VALUES[0] },
    { label: t('K_OPTION_FIXED_TERM', 'Fixed Term'), value: CONTRACT_TYPE_VALUES[1] },
    { label: t('K_OPTION_CASUAL', 'Casual'), value: CONTRACT_TYPE_VALUES[2] },
  ], [t]);

  const SALARY_FREQUENCY_OPTIONS = useMemo(() => [
    { label: t('K_OPTION_MONTHLY', 'Monthly'), value: SALARY_FREQUENCY_VALUES[0] },
    { label: t('K_OPTION_BI_WEEKLY', 'Bi-Weekly'), value: SALARY_FREQUENCY_VALUES[1] },
    { label: t('K_OPTION_WEEKLY', 'Weekly'), value: SALARY_FREQUENCY_VALUES[2] },
  ], [t]);

  // Currency codes are ISO standards — labels kept as-is
  const CURRENCY_OPTIONS = useMemo(() =>
    CURRENCY_VALUES.map((code) => ({ label: code, value: code })),
  []);
  const tenantId = session?.tenant.id ?? '';

  const [resolvedTabs, setResolvedTabs] = useState<FormTemplateTab[]>([]);
  const [formDataMap, setFormDataMap] = useState<Record<string, Record<string, unknown>>>({});
  const [requiredFieldsMap, setRequiredFieldsMap] = useState<Record<string, boolean>>({});
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterError, setFilterError] = useState<string | null>(null);

  const { data: commercialTermsData, isLoading: commercialTermsLoading } = useQuery({
    queryKey: ['commercialTerms', 'TENANT', tenantId],
    queryFn: () => getCommercialTerms('TENANT', tenantId, { size: 100 }),
    enabled: isOpen && !!tenantId,
  });

  const countryOptions = useMemo(() => {
    const terms = commercialTermsData?.content ?? [];
    const unique = new Map<string, string>();
    for (const term of terms) {
      if (!unique.has(term.countryId)) {
        unique.set(term.countryId, term.country.name);
      }
    }
    return Array.from(unique, ([value, label]) => ({ label, value }));
  }, [commercialTermsData]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    trigger,
    getValues,
    watch,
    setValue,
    formState: { errors },
  } = useForm<EmployeeFormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onChange',
  });

  const selectedCountry = watch('country');
  const selectedDepartmentId = watch('departmentId');

  // Cascade — drives Step 2's Department + Job Title Selects (V30 / V6).
  // Capped at 500 rows per dropdown — see features/departments/FEATURE.md
  // "Deferred" checklist for the >500-tenant follow-up.
  const { departments, isLoading: depsLoading } = useDepartmentsForCascade();
  const { jobTitles, isLoading: jtLoading } = useJobTitlesForCascade(
    selectedDepartmentId || undefined,
  );
  const departmentOptions = useMemo(
    () => departments.map((d) => ({ label: d.name, value: d.id })),
    [departments],
  );
  const jobTitleOptions = useMemo(
    () => jobTitles.map((j) => ({ label: j.name, value: j.id })),
    [jobTitles],
  );

  const employmentTypeOptions = useMemo(() => {
    if (!selectedCountry || !commercialTermsData?.content) return [];
    const termsForCountry = commercialTermsData.content.filter(
      (ct) => ct.countryId === selectedCountry,
    );
    const unique = new Map<string, string>();
    for (const term of termsForCountry) {
      if (term.employmentType && !unique.has(term.employmentType.id)) {
        unique.set(term.employmentType.id, term.employmentType.name);
      }
    }
    return Array.from(unique, ([value, label]) => ({ label, value }));
  }, [commercialTermsData, selectedCountry]);

  // Step 1 → 2: Fetch resolved tabs using country + employmentTypeId
  const fetchResolvedTabs = useCallback(async (): Promise<boolean> => {
    const { country, employmentTypeId } = getValues();
    setFilterLoading(true);
    setFilterError(null);
    // formDataMap and requiredFieldsMap intentionally NOT reset —
    // persisted data is restored for forms that survive the filter change

    try {
      let template;
      try {
        template = await getFormTemplateByScope(orgType, orgId, 'EMPLOYEE', {
          countryId: country || undefined,
          employmentTypeId: employmentTypeId || undefined,
        });
      } catch {
        // 404 — no template exists for this country + employment type
        setResolvedTabs([]);
        setFilterLoading(false);
        return true;
      }

      const resolved = await filterFormTemplate(template.id, {});

      setResolvedTabs(resolved.tabs);
      setFilterLoading(false);
      return true;
    } catch {
      setFilterError(t('K_FAILED_LOAD_FORMS', 'Failed to load forms. Please try again.'));
      setFilterLoading(false);
      return false;
    }
  }, [getValues, orgType, orgId, t]);

  const stepper = useMultiStepForm({
    totalSteps: STEPS.length,
    onValidateStep: async (stepIndex) => {
      const fields = STEP_FIELDS[stepIndex];
      if (fields && fields.length > 0) {
        const isValid = await trigger(fields);
        if (!isValid) return false;
      }

      if (stepIndex === 1) {
        return fetchResolvedTabs();
      }

      return true;
    },
  });

  const mandatoryForms = useMemo(() => {
    return resolvedTabs.flatMap((tab) =>
      tab.forms.filter((f) => f.attributes.isMandatory === true),
    );
  }, [resolvedTabs]);

  const mandatoryFormIds = useMemo(() => {
    return mandatoryForms.map((f) => f.formConfigId);
  }, [mandatoryForms]);

  const allMandatoryFormsFilled = useMemo(() => {
    if (mandatoryFormIds.length === 0) return true;
    return mandatoryFormIds.every(
      (id) => formDataMap[id] !== undefined || requiredFieldsMap[id] === false,
    );
  }, [mandatoryFormIds, formDataMap, requiredFieldsMap]);

  const defaultTabs = useMemo(() => {
    return resolvedTabs
      .map((tab) => ({
        ...tab,
        forms: tab.forms.filter((f) => f.attributes.isDefault === true),
      }))
      .filter((tab) => tab.forms.length > 0);
  }, [resolvedTabs]);

  useEffect(() => {
    if (!isOpen) return;
    stepper.reset();
    setResolvedTabs([]);
    setFilterError(null);
    setFormDataMap({});
    setRequiredFieldsMap({});
    reset(DEFAULT_FORM_VALUES);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const createMutation = useMutation({
    mutationFn: createEmployee,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employees'] });
      void queryClient.invalidateQueries({ queryKey: ['employeeCounts'] });
      void queryClient.invalidateQueries({ queryKey: ['allEmployeeCounts'] });
      reset();
      stepper.reset();
      onClose();
    },
    onError: (error) => {
      // NOTE: the BE validateJobTitleAndDepartment failures (INVALID_JOB_TITLE /
      // INVALID_DEPARTMENT / DEPARTMENT_NOT_IN_JOB_TITLE) all collapse to
      // WP_BAD_REQUEST (4000) under the uniform envelope — the backend uses the
      // legacy AppException ctor which discards the discriminating errorCode, so
      // per-field inline errors can no longer be wired. Generic toast for now.
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const handleFormSave = useCallback(
    (formConfigId: string, data: Record<string, unknown>) => {
      setFormDataMap((prev) => ({ ...prev, [formConfigId]: data }));
    },
    [],
  );

  const handleRequiredCheck = useCallback(
    (formConfigId: string, hasRequired: boolean) => {
      setRequiredFieldsMap((prev) => ({ ...prev, [formConfigId]: hasRequired }));
    },
    [],
  );

  const handleFormDefaultToggle = useCallback(
    (tabIndex: number, formIndex: number) => {
      setResolvedTabs((prev) =>
        prev.map((tab, ti) => {
          if (ti !== tabIndex) return tab;
          return {
            ...tab,
            forms: tab.forms.map((form, fi) => {
              if (fi !== formIndex) return form;
              return { ...form, attributes: { ...form.attributes, isDefault: !form.attributes.isDefault } };
            }),
          };
        }),
      );
    },
    [],
  );

  /** Returns formDataMap entries that exist in the current resolvedTabs */
  const getRelevantFormData = useCallback((): Record<string, Record<string, unknown>> | undefined => {
    const currentFormIds = new Set(
      resolvedTabs.flatMap((tab) => tab.forms.map((f) => f.formConfigId)),
    );
    const filtered: Record<string, Record<string, unknown>> = {};
    for (const [id, data] of Object.entries(formDataMap)) {
      if (currentFormIds.has(id)) {
        filtered[id] = data;
      }
    }
    return Object.keys(filtered).length > 0 ? filtered : undefined;
  }, [resolvedTabs, formDataMap]);

  const onSubmit = useCallback(
    (data: EmployeeFormValues) => {
      const payload: CreateEmployeePayload = {
        firstName: data.firstName,
        lastName: data.lastName,
        preferredName: data.preferredName || undefined,
        workEmail: data.workEmail,
        countryId: data.country,
        jobTitle: { id: data.jobTitleId },
        department: { id: data.departmentId },
        employmentTypeId: data.employmentTypeId,
        contractType: data.contractType,
        startDate: toApiDateTime(data.startDate) ?? data.startDate,
        grossSalary: Number(data.grossSalary),
        salaryCurrency: data.salaryCurrency,
        salaryFrequency: data.salaryFrequency,
        probationMonths: data.probationMonths ? Number(data.probationMonths) : undefined,
        weeklyWorkingHours: data.weeklyWorkingHours ? Number(data.weeklyWorkingHours) : undefined,
        tabs: resolvedTabs.length > 0 ? resolvedTabs : undefined,
        formData: getRelevantFormData(),
      };
      createMutation.mutate(payload);
    },
    [createMutation, resolvedTabs, getRelevantFormData],
  );

  const handleClose = useCallback(() => {
    if (!createMutation.isPending) {
      reset();
      stepper.reset();
      setResolvedTabs([]);
      setFilterError(null);
      setFormDataMap({});
      setRequiredFieldsMap({});
      onClose();
    }
  }, [createMutation.isPending, reset, stepper, onClose]);

  const handleNext = useCallback(async () => {
    if (stepper.isLastStep) {
      handleSubmit(onSubmit)();
    } else {
      await stepper.goToNext();
    }
  }, [stepper, handleSubmit, onSubmit]);

  const disableNextOnStep2 = stepper.currentStep === 2 && mandatoryFormIds.length > 0 && !allMandatoryFormsFilled;

  const footer = useMemo(
    () => (
      <StepperModalFooter
        isFirstStep={stepper.isFirstStep}
        isLastStep={stepper.isLastStep}
        onNext={handleNext}
        onPrev={stepper.goToPrev}
        onCancel={handleClose}
        isSubmitting={createMutation.isPending || filterLoading}
        submitLabel={t('K_SAVE', 'Save')}
        disableNext={disableNextOnStep2}
      />
    ),
    [stepper, handleNext, handleClose, createMutation.isPending, filterLoading, disableNextOnStep2],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('K_MODAL_ADD_EMPLOYEE', 'Add Employee')}
      size="lg"
      footer={footer}
    >
      <Stepper
        steps={STEPS}
        activeStep={stepper.currentStep}
        onStepClick={stepper.goToStep}
      />

      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.stepContainer}>
          {/* Step 1: Country & Employment Type */}
          <div
            className={`${styles.step} ${
              stepper.currentStep === 0 ? styles.stepActive : styles.stepHidden
            } ${stepper.direction === 1 ? styles.slideLeft : styles.slideRight}`}
          >
            <div className={styles.row}>
              <Controller
                name="country"
                control={control}
                rules={{ required: t('K_VALIDATION_COUNTRY_REQUIRED', 'Country is required') }}
                render={({ field }) => (
                  <Select
                    label={t('K_LABEL_COUNTRY', 'Country')}
                    isRequired
                    placeholder={t('K_PLACEHOLDER_COUNTRY', 'Select country')}
                    options={countryOptions}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      setValue('employmentTypeId', '');
                    }}
                    error={errors.country?.message}
                    loading={commercialTermsLoading}
                    searchable
                  />
                )}
              />
              <Controller
                name="employmentTypeId"
                control={control}
                rules={{ required: t('K_VALIDATION_EMPLOYMENT_TYPE_REQUIRED', 'Employment type is required') }}
                render={({ field }) => (
                  <Select
                    label={t('K_LABEL_EMPLOYMENT_TYPE', 'Employment Type')}
                    isRequired
                    placeholder={selectedCountry ? t('K_PLACEHOLDER_EMPLOYMENT_TYPE', 'Select employment type') : t('K_PLACEHOLDER_SELECT_COUNTRY_FIRST', 'Select a country first')}
                    options={employmentTypeOptions}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.employmentTypeId?.message}
                    disabled={!selectedCountry}
                  />
                )}
              />
            </div>
          </div>

          {/* Step 2: Employee Details */}
          <div
            className={`${styles.step} ${
              stepper.currentStep === 1 ? styles.stepActive : styles.stepHidden
            } ${stepper.direction === 1 ? styles.slideLeft : styles.slideRight}`}
          >
            <div className={styles.row}>
              <TextInput
                label={t('K_LABEL_FIRST_NAME', 'First Name')}
                isRequired
                placeholder={t('K_PLACEHOLDER_FIRST_NAME', 'Enter first name')}
                error={errors.firstName?.message}
                {...register('firstName', {
                  required: t('K_VALIDATION_FIRST_NAME_REQUIRED', 'First name is required'),
                  maxLength: { value: 255, message: t('K_VALIDATION_MAX_255', 'Max 255 characters') },
                })}
              />
              <TextInput
                label={t('K_LABEL_LAST_NAME', 'Last Name')}
                isRequired
                placeholder={t('K_PLACEHOLDER_LAST_NAME', 'Enter last name')}
                error={errors.lastName?.message}
                {...register('lastName', {
                  required: t('K_VALIDATION_LAST_NAME_REQUIRED', 'Last name is required'),
                  maxLength: { value: 255, message: t('K_VALIDATION_MAX_255', 'Max 255 characters') },
                })}
              />
            </div>

            <div className={styles.row}>
              <TextInput
                label={t('K_LABEL_PREFERRED_NAME', 'Preferred Name')}
                placeholder={t('K_PLACEHOLDER_PREFERRED_NAME', 'Enter preferred name')}
                {...register('preferredName', {
                  maxLength: { value: 255, message: t('K_VALIDATION_MAX_255', 'Max 255 characters') },
                })}
              />
              <TextInput
                label={t('K_LABEL_WORK_EMAIL', 'Work Email')}
                isRequired
                type="email"
                placeholder={t('K_PLACEHOLDER_WORK_EMAIL', 'Enter work email')}
                error={errors.workEmail?.message}
                {...register('workEmail', {
                  required: t('K_VALIDATION_WORK_EMAIL_REQUIRED', 'Work email is required'),
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: t('K_VALIDATION_EMAIL_PATTERN', 'Enter a valid email address'),
                  },
                })}
              />
            </div>

            <div className={styles.row}>
              <Select
                label={t('K_LABEL_COUNTRY', 'Country')}
                options={countryOptions}
                value={selectedCountry}
                onChange={() => {}}
                disabled
              />
              <Select
                label={t('K_LABEL_EMPLOYMENT_TYPE', 'Employment Type')}
                options={employmentTypeOptions}
                value={watch('employmentTypeId')}
                onChange={() => {}}
                disabled
              />
            </div>

            <div className={styles.row}>
              <Controller
                name="departmentId"
                control={control}
                rules={{ required: t('K_VALIDATION_DEPARTMENT_REQUIRED', 'Department is required') }}
                render={({ field }) => (
                  <Select
                    label={t('K_LABEL_DEPARTMENT', 'Department')}
                    isRequired
                    placeholder={t('K_PLACEHOLDER_SELECT_DEPARTMENT', 'Select a department')}
                    options={departmentOptions}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      // Switching dept may invalidate the current JT pick.
                      if (value !== field.value) {
                        setValue('jobTitleId', '', { shouldValidate: true, shouldDirty: true });
                      }
                    }}
                    loading={depsLoading}
                    error={errors.departmentId?.message}
                  />
                )}
              />
              <Controller
                name="jobTitleId"
                control={control}
                rules={{ required: t('K_VALIDATION_JOB_TITLE_REQUIRED', 'Job title is required') }}
                render={({ field }) => (
                  <Select
                    label={t('K_LABEL_JOB_TITLE', 'Job Title')}
                    isRequired
                    placeholder={
                      !selectedDepartmentId
                        ? t('K_PLACEHOLDER_SELECT_DEPARTMENT', 'Select a department')
                        : t('K_PLACEHOLDER_SELECT_JOB_TITLE', 'Select a job title')
                    }
                    options={jobTitleOptions}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!selectedDepartmentId}
                    loading={jtLoading}
                    error={errors.jobTitleId?.message}
                  />
                )}
              />
            </div>

            <div className={styles.row}>
              <Controller
                name="contractType"
                control={control}
                rules={{ required: t('K_VALIDATION_CONTRACT_TYPE_REQUIRED', 'Contract type is required') }}
                render={({ field }) => (
                  <Select
                    label={t('K_LABEL_CONTRACT_TYPE', 'Contract Type')}
                    isRequired
                    placeholder={t('K_PLACEHOLDER_CONTRACT_TYPE', 'Select contract type')}
                    options={CONTRACT_TYPE_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.contractType?.message}
                  />
                )}
              />
              <Controller
                name="startDate"
                control={control}
                rules={{ required: t('K_VALIDATION_START_DATE_REQUIRED', 'Start date is required') }}
                render={({ field }) => (
                  <DatePicker
                    label={t('K_LABEL_START_DATE', 'Start Date')}
                    isRequired
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.startDate?.message}
                    locale="en-GB"
                    fullWidth
                  />
                )}
              />
            </div>

            <div className={styles.row}>
              <TextInput
                label={t('K_LABEL_GROSS_SALARY', 'Gross Salary')}
                isRequired
                type="number"
                placeholder={t('K_PLACEHOLDER_GROSS_SALARY', 'Enter gross salary')}
                error={errors.grossSalary?.message}
                {...register('grossSalary', {
                  required: t('K_VALIDATION_GROSS_SALARY_REQUIRED', 'Gross salary is required'),
                  min: { value: 0, message: t('K_VALIDATION_SALARY_POSITIVE', 'Salary must be positive') },
                })}
              />
              <Controller
                name="salaryCurrency"
                control={control}
                rules={{ required: t('K_VALIDATION_CURRENCY_REQUIRED', 'Currency is required') }}
                render={({ field }) => (
                  <Select
                    label={t('K_LABEL_SALARY_CURRENCY', 'Salary Currency')}
                    isRequired
                    placeholder={t('K_PLACEHOLDER_CURRENCY', 'Select currency')}
                    options={CURRENCY_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.salaryCurrency?.message}
                    searchable
                  />
                )}
              />
            </div>

            <div className={styles.row}>
              <Controller
                name="salaryFrequency"
                control={control}
                rules={{ required: t('K_VALIDATION_SALARY_FREQUENCY_REQUIRED', 'Salary frequency is required') }}
                render={({ field }) => (
                  <Select
                    label={t('K_LABEL_SALARY_FREQUENCY', 'Salary Frequency')}
                    isRequired
                    placeholder={t('K_PLACEHOLDER_FREQUENCY', 'Select frequency')}
                    options={SALARY_FREQUENCY_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.salaryFrequency?.message}
                  />
                )}
              />
              <TextInput
                label={t('K_LABEL_PROBATION_PERIOD', 'Probation Period (Months)')}
                type="number"
                placeholder={t('K_PLACEHOLDER_PROBATION_MONTHS', 'e.g. 3')}
                {...register('probationMonths', {
                  min: { value: 0, message: t('K_VALIDATION_MUST_BE_POSITIVE', 'Must be positive') },
                })}
              />
            </div>

            <div className={styles.row}>
              <TextInput
                label={t('K_LABEL_WEEKLY_WORKING_HOURS', 'Weekly Working Hours')}
                type="number"
                placeholder={t('K_PLACEHOLDER_WEEKLY_HOURS', 'e.g. 40')}
                {...register('weeklyWorkingHours', {
                  min: { value: 0, message: t('K_VALIDATION_MUST_BE_POSITIVE', 'Must be positive') },
                })}
              />
            </div>
          </div>

          {/* Step 3: Mandatory Forms */}
          <div
            className={`${styles.step} ${
              stepper.currentStep === 2 ? styles.stepActive : styles.stepHidden
            } ${stepper.direction === 1 ? styles.slideLeft : styles.slideRight}`}
          >
            {filterLoading && (
              <p className={styles.placeholderText}>{t('K_LOADING_FORMS', 'Loading forms...')}</p>
            )}

            {filterError && (
              <p className={styles.errorText}>{filterError}</p>
            )}

            {!filterLoading && !filterError && mandatoryForms.length === 0 && (
              <p className={styles.placeholderText}>
                {t('K_NO_MANDATORY_FORMS', 'No mandatory forms to fill.')}
              </p>
            )}

            {!filterLoading && !filterError && mandatoryForms.length > 0 && (
              <div className={styles.tabAccordions}>
                {mandatoryForms.map((form) => (
                  <MandatoryFormRenderer
                    key={form.formConfigId}
                    formConfigId={form.formConfigId}
                    formName={form.formName ?? form.formConfigId}
                    initialSaved={formDataMap[form.formConfigId] !== undefined}
                    initialData={formDataMap[form.formConfigId]}
                    onSave={handleFormSave}
                    onRequiredCheck={handleRequiredCheck}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Step 4: Requested Data — default forms with checkboxes */}
          <div
            className={`${styles.step} ${
              stepper.currentStep === 3 ? styles.stepActive : styles.stepHidden
            } ${stepper.direction === 1 ? styles.slideLeft : styles.slideRight}`}
          >
            {defaultTabs.length > 0 && (
              <div className={styles.tabAccordions}>
                {resolvedTabs.map((tab, tabIndex) => (
                  <Accordion
                    key={tab.name + String(tabIndex)}
                    title={tab.name}
                    variant="filled"
                    defaultExpanded={tabIndex === 0}
                  >
                    <div className={styles.formCheckboxList}>
                      {tab.forms.map((form, formIndex) => (
                        <label
                          key={form.formConfigId + String(formIndex)}
                          className={styles.formCheckboxItem}
                        >
                          <input
                            type="checkbox"
                            checked={form.attributes.isDefault}
                            onChange={() => handleFormDefaultToggle(tabIndex, formIndex)}
                            className={styles.formCheckbox}
                          />
                          <span className={styles.formCheckboxLabel}>
                            {form.formName ?? form.formConfigId}
                          </span>
                        </label>
                      ))}
                      {tab.forms.length === 0 && (
                        <p className={styles.noFormsText}>{t('K_NO_FORMS_IN_TAB', 'No forms in this tab.')}</p>
                      )}
                    </div>
                  </Accordion>
                ))}
              </div>
            )}
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default CreateEmployeeModal;
